import {
  ACCOUNT_COOKIE_NAME,
  ACCOUNT_RECONCILED_AT_STORAGE_NAME,
  ACCOUNT_RECONCILE_TTL,
  JWT_REFRESH_INTERVAL,
  JWT_REFRESH_RETRY_BACKOFF,
  LOGIN_URL,
  SESSION_COOKIE_STATUS_URL,
  SESSION_URL,
  SSO_HINT_COOKIE_NAME
} from "./chunk-NIGJJYQN.mjs";
import {
  refreshSubpathJWTStatus
} from "./chunk-YUMKHUP4.mjs";

// src/utils/buildAccountSwitcherUrl.ts
function buildAccountSwitcherUrl(accountId) {
  return `/switch-account?account=${accountId}&next=${window.location.pathname}`;
}

// src/utils/buildLoginUrl.ts
import { addTruthyParamsToUrl } from "@zapier/url-utils";
function buildLoginUrl() {
  const next = window.location.pathname + window.location.search + window.location.hash;
  return addTruthyParamsToUrl(LOGIN_URL, { next });
}

// src/IdentityProvider/IdentityProvider.tsx
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { readCookie as readCookie3, writeCookie as writeCookie2 } from "@zapier/cookies";

// src/utils/getAccountIdFromCookie.ts
import { addTruthyParamsToUrl as addTruthyParamsToUrl2 } from "@zapier/url-utils";
import { readCookie, writeCookie } from "@zapier/cookies";

// src/utils/getCurrentAccountCookieDomain.ts
function getCurrentAccountCookieDomain(hostname) {
  const host = hostname ?? (typeof window !== "undefined" ? window.location.hostname : void 0);
  if (!host) return void 0;
  const normalizedHost = host.toLowerCase().replace(/\.$/, "");
  if (!normalizedHost || normalizedHost === "localhost") return void 0;
  const rawOrigin = process.env.NEXT_PUBLIC_ZAPIER_ORIGIN;
  if (!rawOrigin) return void 0;
  let originHost;
  try {
    originHost = new URL(rawOrigin).hostname;
  } catch {
    return void 0;
  }
  if (!originHost || originHost === "localhost") return void 0;
  if (normalizedHost === originHost || normalizedHost.endsWith("." + originHost)) {
    return "." + originHost;
  }
  return void 0;
}

// src/utils/getAccountIdFromCookie.ts
var EXPIRED_DATE = new Date(Date.now() - 10);
function getAccountIdFromCookie() {
  if (typeof document === "undefined") {
    return void 0;
  }
  const cookies = document.cookie.split(";");
  const currentAccountIdCookies = cookies.filter(
    (cookie) => cookie.includes(ACCOUNT_COOKIE_NAME)
  );
  const allCookiesAreTheSame = currentAccountIdCookies.every(
    (cookie) => cookie.trim() === currentAccountIdCookies[0].trim()
  );
  if (currentAccountIdCookies.length > 1 && !allCookiesAreTheSame) {
    writeCookie(ACCOUNT_COOKIE_NAME, "", {
      category: "essential",
      expires: EXPIRED_DATE,
      path: "/"
    });
    const cookieDomain = getCurrentAccountCookieDomain();
    if (cookieDomain) {
      writeCookie(ACCOUNT_COOKIE_NAME, "", {
        category: "essential",
        expires: EXPIRED_DATE,
        path: "/",
        domain: cookieDomain
      });
    }
    const next = new URLSearchParams(window.location.search).get("next");
    window.location.href = addTruthyParamsToUrl2(LOGIN_URL, {
      next,
      reauth: true
    });
    return void 0;
  }
  const accountId = readCookie(ACCOUNT_COOKIE_NAME);
  if (!accountId) {
    return void 0;
  }
  const parsedAccountId = parseInt(accountId, 10);
  if (isNaN(parsedAccountId)) {
    return void 0;
  }
  return parsedAccountId;
}

// src/client/fetchSession.ts
async function fetchSession() {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_ZAPIER_ORIGIN}${SESSION_URL}`,
    {
      credentials: "include"
    }
  );
  const session = await response.json();
  return session;
}

// src/utils/getSsoHintFromCookie.ts
import { readCookie as readCookie2 } from "@zapier/cookies";
function getSsoHintFromCookie() {
  if (typeof document === "undefined") {
    return void 0;
  }
  const ssoHint = readCookie2(SSO_HINT_COOKIE_NAME);
  if (!ssoHint || ssoHint === "null" || ssoHint === "undefined") {
    return void 0;
  }
  return ssoHint;
}

// src/IdentityProvider/IdentityProvider.tsx
import { jsx } from "react/jsx-runtime";
function reconcileTtlElapsed() {
  try {
    const reconciledAt = Number(
      window.localStorage.getItem(ACCOUNT_RECONCILED_AT_STORAGE_NAME)
    );
    const age = Date.now() - reconciledAt;
    return !Number.isFinite(age) || age < 0 || age >= ACCOUNT_RECONCILE_TTL;
  } catch {
    return true;
  }
}
function markReconcileAttempt() {
  try {
    window.localStorage.setItem(
      ACCOUNT_RECONCILED_AT_STORAGE_NAME,
      String(Date.now())
    );
  } catch {
  }
}
var IdentityContext = createContext({
  currentAccountId: void 0,
  ssoHint: void 0
});
var useIdentity = () => useContext(IdentityContext);
function IdentityProvider({
  currentAccountId: currentAccountIdProp,
  ...props
}) {
  const [currentAccountId, setCurrentAccountId] = useState(
    currentAccountIdProp ?? getAccountIdFromCookie()
  );
  const [ssoHint, setSsoHint] = useState(
    getSsoHintFromCookie()
  );
  async function watchCookies() {
    const accountIdFromCookie = getAccountIdFromCookie();
    const ssoHintFromCookie = getSsoHintFromCookie();
    if (accountIdFromCookie && accountIdFromCookie !== currentAccountId) {
      setCurrentAccountId(accountIdFromCookie);
    }
    if (ssoHintFromCookie !== ssoHint) {
      setSsoHint(ssoHintFromCookie);
    }
  }
  const checkSession = useCallback(async () => {
    try {
      const session = await fetchSession();
      if (!session?.current_account_id) {
        return;
      }
      const sessionAccountId = session.current_account_id;
      const cookieAccountId = readCookie3(ACCOUNT_COOKIE_NAME);
      if (String(cookieAccountId) === String(sessionAccountId)) {
        return;
      }
      const cookieDomain = getCurrentAccountCookieDomain();
      writeCookie2("currentAccountId", sessionAccountId, {
        path: "/",
        sameSite: "strict",
        category: "essential",
        ...cookieDomain !== void 0 ? { domain: cookieDomain } : {}
      });
    } catch (error) {
      console.error("Failed to fetch session:", error);
    }
  }, []);
  useEffect(() => {
    if (currentAccountIdProp !== void 0) {
      setCurrentAccountId(currentAccountIdProp);
      return;
    }
    const timer = setInterval(watchCookies, 1e3);
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [currentAccountIdProp, currentAccountId, ssoHint]);
  useEffect(() => {
    if (currentAccountIdProp !== void 0) {
      return;
    }
    const hasAccountCookie = Boolean(readCookie3(ACCOUNT_COOKIE_NAME));
    if (hasAccountCookie && !reconcileTtlElapsed()) {
      return;
    }
    markReconcileAttempt();
    void checkSession();
  }, [currentAccountIdProp, checkSession]);
  const identityValue = useMemo(
    () => ({
      currentAccountId,
      ssoHint
    }),
    [currentAccountId, ssoHint]
  );
  return /* @__PURE__ */ jsx(IdentityContext.Provider, { value: identityValue, children: props.children });
}

// src/IdentityProvider/useCurrentAccountId.ts
function useCurrentAccountId() {
  const { currentAccountId } = useIdentity();
  return currentAccountId;
}

// src/IdentityProvider/useSsoHint.ts
function useSsoHint() {
  const { ssoHint } = useIdentity();
  return ssoHint;
}

// src/SessionProvider/SessionProvider.tsx
import {
  createContext as createContext2,
  useCallback as useCallback3,
  useEffect as useEffect3,
  useMemo as useMemo2,
  useRef,
  useState as useState4
} from "react";
import { useInterval } from "usehooks-ts";

// src/utils/useDocumentIsHidden.ts
import { useEffect as useEffect2, useState as useState2 } from "react";
function useDocumentIsHidden() {
  const [isHidden, setIsHidden] = useState2(false);
  useEffect2(() => {
    const abortController = new AbortController();
    const { signal } = abortController;
    addEventListener(
      "visibilitychange",
      () => {
        setIsHidden(document.hidden);
      },
      { signal }
    );
    return () => {
      abortController.abort();
    };
  }, []);
  return isHidden;
}

// src/SessionProvider/useJWTRefresh.ts
import { readCookies } from "@zapier/cookies";
import { useCallback as useCallback2, useState as useState3 } from "react";

// src/SessionProvider/types.ts
var SessionStatus = {
  Loading: "loading",
  // no settled answer yet (first load, transient failure, or throttle-skip)
  Authenticated: "authenticated",
  // the last validation confirmed a valid session
  Unauthenticated: "unauthenticated"
  // signed out (anonymous ssohint or a refresh 401/403)
};

// src/SessionProvider/useJWTRefresh.ts
var isInconclusive = (status) => status !== 200 && status !== 401 && status !== 403;
var delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
function useJWTRefresh() {
  const [isValidating, setIsValidating] = useState3(false);
  const performRefresh = useCallback2(async () => {
    const cookies = readCookies();
    if (cookies.ssohint === "anonymous") {
      return SessionStatus.Unauthenticated;
    }
    setIsValidating(true);
    try {
      let status = await refreshSubpathJWTStatus();
      if (isInconclusive(status)) {
        status = await refreshSubpathJWTStatus();
      }
      if (isInconclusive(status)) {
        await delay(JWT_REFRESH_RETRY_BACKOFF);
        status = await refreshSubpathJWTStatus();
      }
      if (status === 200) {
        return SessionStatus.Authenticated;
      }
      if (status === 401 || status === 403) {
        return SessionStatus.Unauthenticated;
      }
      return null;
    } finally {
      setIsValidating(false);
    }
  }, []);
  return {
    performRefresh,
    isValidating
  };
}

// src/client/sessionCookieStatus.ts
async function fetchSessionCookieStatus() {
  try {
    const response = await fetch(SESSION_COOKIE_STATUS_URL, {
      credentials: "include"
    });
    return response.status;
  } catch {
    return 0;
  }
}

// src/sessionReady.ts
var SESSION_READY_TIMEOUT_MS = 15e3;
var STATE_KEY = "__zapier_identity_session_ready__";
var globals = globalThis;
var isServer = () => typeof window === "undefined";
var getState = () => globals[STATE_KEY] ??= {
  deferredAuthPromise: null,
  ready: false,
  timeoutId: null
};
var clearFailsafe = (state) => {
  if (state.timeoutId !== null) {
    clearTimeout(state.timeoutId);
    state.timeoutId = null;
  }
};
function markSessionReady() {
  if (isServer()) return;
  const state = getState();
  clearFailsafe(state);
  state.ready = true;
  if (state.deferredAuthPromise) {
    state.deferredAuthPromise.resolved = true;
    state.deferredAuthPromise.resolve();
  }
}
function sessionJwtIsAvailable() {
  if (isServer()) return Promise.resolve();
  const state = getState();
  if (state.ready) return Promise.resolve();
  if (!state.deferredAuthPromise) {
    let resolve;
    const promise = new Promise((res) => {
      resolve = res;
    });
    state.deferredAuthPromise = { promise, resolve, resolved: false };
    state.timeoutId = setTimeout(markSessionReady, SESSION_READY_TIMEOUT_MS);
  }
  return state.deferredAuthPromise.promise;
}
function closeSessionReady() {
  const state = getState();
  state.ready = false;
  if (state.deferredAuthPromise && !state.deferredAuthPromise.resolved) {
    clearFailsafe(state);
    state.timeoutId = setTimeout(markSessionReady, SESSION_READY_TIMEOUT_MS);
    return;
  }
  clearFailsafe(state);
  state.deferredAuthPromise = null;
}

// src/SessionProvider/SessionProvider.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var DEFAULT_SESSION_STATE = {
  status: SessionStatus.Loading,
  isAuthed: false,
  isValidating: false,
  isLoading: false
};
var SessionContext = createContext2(
  DEFAULT_SESSION_STATE
);
var SessionProvider = ({
  children,
  blockRenderUntilVerified = false,
  fallback = null,
  redirectOnUnauthenticated = true
}) => {
  const currentAccountId = useCurrentAccountId();
  const ssoHint = useSsoHint();
  const isHidden = useDocumentIsHidden();
  const redirectToLogin = useCallback3(() => {
    if (!redirectOnUnauthenticated || window.location.pathname === LOGIN_URL) {
      return;
    }
    window.location.href = buildLoginUrl();
  }, [redirectOnUnauthenticated]);
  const refresh = useJWTRefresh();
  const [status, setStatus] = useState4(SessionStatus.Loading);
  const [checkInFlight, setCheckInFlight] = useState4(false);
  const isValidating = refresh.isValidating || checkInFlight;
  const runRefresh = useCallback3(async () => {
    const outcome = await refresh.performRefresh();
    if (outcome) {
      setStatus(outcome);
    }
  }, [refresh.performRefresh]);
  const changeMountedRef = useRef(false);
  const lastIdentityRef = useRef(null);
  useEffect3(() => {
    const firstRun = !changeMountedRef.current;
    changeMountedRef.current = true;
    if (isHidden) {
      return;
    }
    const identity = `${currentAccountId}|${ssoHint}`;
    if (firstRun) {
      lastIdentityRef.current = identity;
      return;
    }
    if (identity !== lastIdentityRef.current) {
      lastIdentityRef.current = identity;
      closeSessionReady();
    }
    runRefresh();
  }, [currentAccountId, ssoHint, isHidden, runRefresh]);
  useEffect3(() => {
    if (isHidden) {
      return;
    }
    if (ssoHint === "anonymous") {
      setStatus(SessionStatus.Unauthenticated);
      return;
    }
    let cancelled = false;
    let cheapConfirmed = false;
    setCheckInFlight(true);
    const cheapCheck = fetchSessionCookieStatus();
    const refreshOutcome = refresh.performRefresh();
    cheapCheck.then((cookieStatus) => {
      if (!cancelled && cookieStatus === 200) {
        cheapConfirmed = true;
        setStatus(SessionStatus.Authenticated);
      }
    }).finally(() => {
      if (!cancelled) {
        setCheckInFlight(false);
      }
    });
    Promise.all([cheapCheck, refreshOutcome]).then(([, outcome]) => {
      if (cancelled || cheapConfirmed) {
        return;
      }
      if (outcome === SessionStatus.Authenticated) {
        setStatus(SessionStatus.Authenticated);
        return;
      }
      if (outcome === SessionStatus.Unauthenticated) {
        setStatus(SessionStatus.Unauthenticated);
        redirectToLogin();
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect3(() => {
    if (blockRenderUntilVerified && status === SessionStatus.Unauthenticated) {
      redirectToLogin();
    }
  }, [blockRenderUntilVerified, status, redirectToLogin]);
  useInterval(
    () => {
      runRefresh();
    },
    isHidden ? null : JWT_REFRESH_INTERVAL
  );
  const session = useMemo2(
    () => ({
      status,
      isAuthed: status === SessionStatus.Authenticated,
      isValidating,
      isLoading: status === SessionStatus.Loading && isValidating
    }),
    [status, isValidating]
  );
  useEffect3(() => {
    if (session.isAuthed && !session.isValidating) {
      markSessionReady();
    }
  }, [session.isAuthed, session.isValidating]);
  const blocked = blockRenderUntilVerified && status !== SessionStatus.Authenticated;
  return /* @__PURE__ */ jsx2(SessionContext.Provider, { value: session, children: blocked ? fallback : children });
};

// src/SessionProvider/useSession.ts
import { useContext as useContext2 } from "react";
function useSession() {
  return useContext2(SessionContext);
}
export {
  ACCOUNT_COOKIE_NAME,
  IdentityProvider,
  SSO_HINT_COOKIE_NAME,
  SessionProvider,
  SessionStatus,
  buildAccountSwitcherUrl,
  buildLoginUrl,
  sessionJwtIsAvailable,
  useCurrentAccountId,
  useSession,
  useSsoHint
};
//# sourceMappingURL=index.mjs.map